import {
  yup,
  strictObjectSchema,
  makeOptionalAndRequiredSchemas,
  makeOptionalAndRequiredObjectSchemas,
  isEmpty,
  isInteger,
  ignoredTestName,
} from './validationLib.js';
import {
  DisclosureItem,
  FundingSource,
  BeneficiaryRelationship,
  BeneficiaryType,
  MaritalStatus,
  EmploymentStatus,
  Language,
  AccountLossRiskQuestion,
  InvestmentObjectiveRiskQuestion,
  IncomeLevel,
  NetWorth,
  Country,
  ResidencyStatus,
  VisaType,
  PayrollAdminType,
} from '../constants/userConstants.js';
import { PayrollElectionType } from '../constants/employmentConstants.js';
import { SystemValidationError } from '../constants/errorCodes.js';
import { isValidCalendarDate } from '../util/time.js';

const {
  REQUIRED_SYSTEM_HANDLED,
  UNEXPECTED_SYSTEM_HANDLED,
  INVALID_ACCOUNT_OPERATION,
  INVALID_ACCOUNT_STATE_FOR_REQUEST,
} = SystemValidationError;

const isOnboarding = (context) => !context.onboardingComplete;
const isUserOnboarding = (context) => !context.isPayrollAdmin && isOnboarding(context);

const requiredToBeUserOnboarding = [INVALID_ACCOUNT_STATE_FOR_REQUEST, isUserOnboarding];
const requiredToBeOnboarding = [INVALID_ACCOUNT_STATE_FOR_REQUEST, isOnboarding];

const baseNameSchema = yup.string().min(2, 'minLength');

//TODO what should the max length be? for these fields and others as well
const [, givenNameRequiredSchema] = makeOptionalAndRequiredSchemas(baseNameSchema.label('givenName'));

const [middleNameOptionalSchema] = makeOptionalAndRequiredSchemas(baseNameSchema.label('middleName'));

const [, familyNameRequiredSchema] = makeOptionalAndRequiredSchemas(baseNameSchema.label('familyName'));

const [emailOptionalSchema, emailRequiredSchema] = makeOptionalAndRequiredSchemas(
  yup.string().label('email').email('invalidEmail'),
);

const [phoneNumberOptionalSchema, phoneNumberRequiredSchema] = makeOptionalAndRequiredSchemas(
  yup
    .string()
    .label('phoneNumber')
    .test(ignoredTestName, 'validationMessage', (value) => {
      if (isEmpty(value)) return true;
      return /^\d{3}-\d{3}-\d{4}$/.test(value);
    }),
);

const textMessagingApprovedRequiredSchema = yup
  .boolean()
  .label('textMessagingApproved')
  .required(REQUIRED_SYSTEM_HANDLED);

//the requirements for passwords will be set by firebase configuration
const passwordSchemaBase = yup.stringUntrimmed().required('required');
const passwordSchema = passwordSchemaBase.label('password');
const currentPasswordSchema = passwordSchemaBase.label('currentPassword');
const newPasswordSchema = passwordSchema
  .label('newPassword')
  .test(ignoredTestName, 'cannotMatchCurrent', function (newPassword) {
    return newPassword !== this.parent.currentPassword;
  });

const newPasswordRepeatedSchema = passwordSchema
  .label('newPasswordRepeated')
  .test(ignoredTestName, 'notMatching', function (newPasswordRepeated) {
    return newPasswordRepeated === this.parent.newPassword;
  });

const [languagePreferenceOptionalSchema, languagePreferenceRequiredSchema] = makeOptionalAndRequiredSchemas(
  yup.string().label('languagePreference').oneOf(Language.ALL_ITEMS, 'validationMessage').required('required'),
);

const isPayrollAdminSchema = yup.boolean().optional();

const createAccountWithEmailAndPasswordSchema = strictObjectSchema({
  email: emailRequiredSchema,
  password: passwordSchema,
});

const [activationCodeOptionalSchema, activationCodeRequiredSchema] = makeOptionalAndRequiredSchemas(
  yup.string().label('activationCode'),
  UNEXPECTED_SYSTEM_HANDLED,
);

const createUserSchema = strictObjectSchema({
  languagePreference: languagePreferenceOptionalSchema,
  isPayrollAdmin: isPayrollAdminSchema,
  activationCode: activationCodeOptionalSchema,
});

const [taxIdOptionalSchema, taxIdRequiredSchema] = makeOptionalAndRequiredSchemas(
  yup
    .string()
    .label('taxId')
    .matches(/^\d{3}-\d{2}-\d{4}$/, 'validationMessage'),
);

const loginUserSchema = strictObjectSchema({
  email: emailRequiredSchema,
  password: passwordSchema,
});

const activateUserEmploymentSchema = strictObjectSchema({
  activationCode: activationCodeRequiredSchema,
  taxId: taxIdRequiredSchema,
});

const [nameOptionalSchema, nameRequiredSchema] = makeOptionalAndRequiredObjectSchemas({
  givenName: givenNameRequiredSchema,
  middleName: middleNameOptionalSchema,
  familyName: familyNameRequiredSchema,
});

const updateUserNameClientSchema = strictObjectSchema({
  name: nameRequiredSchema,
});

const updateUserNameSchema = updateUserNameClientSchema.testContext(...requiredToBeOnboarding);

const updateUserLanguagePreferenceSchema = strictObjectSchema({
  languagePreference: languagePreferenceRequiredSchema,
});

const [addressOptionalSchema, addressRequiredSchema] = makeOptionalAndRequiredObjectSchemas({
  street: yup.string().label('addressStreet').required('required').min(5, 'minLength').max(80, 'maxLength'),

  unit: yup.string().label('addressUnit').optional().nullable().min(1, 'minLength').max(50, 'maxLength'),

  city: yup
    .string()
    .label('addressCity')
    .required('required')
    .matches(/^[A-Za-z\s]+$/, 'validationMessage')
    .min(2, 'minLength')
    .max(100, 'maxLength'),

  state: yup
    .string()
    .label('addressState')
    .required('required')
    .matches(/^[A-Za-z]{2}$/, 'validationMessage'),

  zip: yup
    .string()
    .label('addressZip')
    .required('required')
    .matches(/^\d{5}(-\d{4})?$/, 'validationMessage'),

  country: yup.string().label('addressCountry').required('required').matches(/^USA$/, 'validationMessage'),
});

const updateUserAddressSchema = strictObjectSchema({
  address: addressRequiredSchema,
});

const USER_AGE_MIN = 18;
const USER_AGE_MAX = 73;

const BENEFICIARY_AGE_MIN = 0;
const BENEFICIARY_AGE_MAX = 100;

const calculateAge = (birthdayStr, todayStr) => {
  const birthday = new Date(birthdayStr);
  const today = new Date(todayStr);

  return (
    today.getFullYear() -
    birthday.getFullYear() -
    (today < new Date(today.getFullYear(), birthday.getMonth(), birthday.getDate()) ? 1 : 0)
  );
};

const ageTester = (ageTest) =>
  function (value) {
    const { today } = this.options.context();
    return (
      value == undefined ||
      !isValidCalendarDate(value) ||
      (isValidCalendarDate(today) && ageTest(calculateAge(value, today)))
    );
  };

const minAgeTester = (minAge) => ageTester((age) => age >= minAge);
const maxAgeTester = (maxAge) => ageTester((age) => age <= maxAge);

const birthdaySchema = yup
  .string()
  .label('birthday')
  .test(ignoredTestName, 'validationMessage', (value) => !value || isValidCalendarDate(value));

const [userBirthdayOptionalSchema, userBirthdayRequiredSchema] = makeOptionalAndRequiredSchemas(
  birthdaySchema
    .test(ignoredTestName, 'minUserAge', minAgeTester(USER_AGE_MIN))
    .test(ignoredTestName, 'maxUserAge', maxAgeTester(USER_AGE_MAX)),
);

const [, /*optional ignored*/ beneficiaryBirthdayRequiredSchema] = makeOptionalAndRequiredSchemas(
  birthdaySchema
    .test(ignoredTestName, 'minBeneficiaryAge', minAgeTester(BENEFICIARY_AGE_MIN))
    .test(ignoredTestName, 'maxBeneficiaryAge', maxAgeTester(BENEFICIARY_AGE_MAX)),
);

const updateUserBirthdayClientSchema = strictObjectSchema({
  birthday: userBirthdayRequiredSchema,
});

const updateUserBirthdaySchema = updateUserBirthdayClientSchema.testContext(...requiredToBeUserOnboarding);

const updateUserTaxIdClientSchema = strictObjectSchema({
  taxId: taxIdRequiredSchema,
});

const updateUserTaxIdSchema = updateUserTaxIdClientSchema.testContext(...requiredToBeUserOnboarding);

const employmentStatusSchema = yup
  .string()
  .label('employmentStatus')
  .oneOf(EmploymentStatus.ALL_ITEMS, 'validationMessage')
  .required('required');

const updateUserEmploymentStatusSchema = strictObjectSchema({
  employmentStatus: employmentStatusSchema,
});

const accountLossRiskQuestionSchema = yup
  .string()
  .label('accountLossRiskQuestion')
  .oneOf(AccountLossRiskQuestion.ALL_ITEMS, 'validationMessage')
  .required('required');

const updateUserAccountLossRiskQuestionSchema = strictObjectSchema({
  accountLossRiskQuestion: accountLossRiskQuestionSchema,
});

const investmentObjectiveRiskQuestionSchema = yup
  .string()
  .label('investmentObjectiveRiskQuestion')
  .oneOf(InvestmentObjectiveRiskQuestion.ALL_ITEMS, 'validationMessage')
  .required('required');

const updateUserInvestmentObjectiveRiskQuestionSchema = strictObjectSchema({
  investmentObjectiveRiskQuestion: investmentObjectiveRiskQuestionSchema,
});

const fundingSourceSchema = yup
  .string()
  .label('fundingSource')
  .oneOf(FundingSource.ALL_ITEMS, 'validationMessage')
  .required('required');

const updateUserFundingSourceSchema = strictObjectSchema({
  fundingSource: fundingSourceSchema,
});

const incomeLevelSchema = yup
  .string()
  .label('incomeLevel')
  .oneOf(IncomeLevel.ALL_ITEMS, 'validationMessage')
  .required('required');

const updateUserIncomeLevelSchema = strictObjectSchema({
  incomeLevel: incomeLevelSchema,
});

const netWorthSchema = yup
  .string()
  .label('netWorth')
  .oneOf(NetWorth.ALL_ITEMS, 'validationMessage')
  .required('required');

const updateUserNetWorthSchema = strictObjectSchema({
  netWorth: netWorthSchema,
});

//these handle the client side version or the server side version of the validation
const isUSCitizen = (field) =>
  field.parent.countryOfCitizenship === 'USA' || field.parent.residencyStatusType === ResidencyStatus.CITIZEN;

const isUSCitizenOrPermanentResident = (field) =>
  isUSCitizen(field) ||
  field.parent.isUSPermanentResident ||
  field.parent.residencyStatusType === ResidencyStatus.PERMANENT_RESIDENT;

const residencyStatusTypeSchema = yup
  .string()
  .label('residencyStatusType')
  .required('required')
  .oneOf(ResidencyStatus.ALL_ITEMS, 'required');

const countryCodeSchema = yup.string().oneOf(Country.ALL_ITEMS, UNEXPECTED_SYSTEM_HANDLED);
const countryOfCitizenshipSchema = countryCodeSchema.label('countryOfCitizenship').required('required');
const countryOfBirthSchema = countryCodeSchema
  .label('countryOfBirth')
  .test(ignoredTestName, 'validationMessage', function (value) {
    return isUSCitizen(this) ? isEmpty(value) : !isEmpty(value);
  });

const isUSPermanentResidentSchema = yup
  .boolean()
  .label('isUSPermanentResident')
  .test(ignoredTestName, 'validationMessage', function (value) {
    return isUSCitizen(this) ? isEmpty(value) : !isEmpty(value);
  });

const dateIsMoreThan90DaysFromToday = (field, value) => {
  const { today } = field.options.context();
  if (!isValidCalendarDate(value) || !isValidCalendarDate(today)) return false;

  const expirationDate = new Date(value);
  const todayDate = new Date(today);
  const daysDiff = (expirationDate - todayDate) / (1000 * 60 * 60 * 24);
  return daysDiff >= 90;
};

const visaExpirationDateSchema = yup
  .string()
  .label('visaExpirationDate')
  .test(ignoredTestName, 'validationMessage', function (value) {
    if (isUSCitizenOrPermanentResident(this)) return isEmpty(value);
    return dateIsMoreThan90DaysFromToday(this, value);
  });

const visaTypeSchema = yup
  .string()
  .label('visaType')
  .test(ignoredTestName, UNEXPECTED_SYSTEM_HANDLED, function (value) {
    if (isUSCitizenOrPermanentResident(this)) return !value;
    if (!value) return true; //optional to provide
    return VisaType.ALL_ITEMS.includes(value);
  });

const dateOfDepartureFromUSSchema = yup
  .string()
  .label('dateOfDepartureFromUS')
  .test(ignoredTestName, 'validationMessage', function (value) {
    if (isUSCitizenOrPermanentResident(this)) return !value;
    if (this.parent.visaType !== 'B1' && this.parent.visaType !== 'B2') return !value;
    if (!value) return true; //optional to provide
    return dateIsMoreThan90DaysFromToday(this, value);
  });

const residencyStatusSchema = strictObjectSchema({
  countryOfCitizenship: countryOfCitizenshipSchema,
  countryOfBirth: countryOfBirthSchema,
  isUSPermanentResident: isUSPermanentResidentSchema,
  visaExpirationDate: visaExpirationDateSchema,
  visaType: visaTypeSchema,
  dateOfDepartureFromUS: dateOfDepartureFromUSSchema,
});

const updateUserResidencyStatusSchema = strictObjectSchema({
  residencyStatus: residencyStatusSchema.required(REQUIRED_SYSTEM_HANDLED),
}).testContext(...requiredToBeUserOnboarding);

const residencyStatusClientSchema = strictObjectSchema({
  residencyStatusType: residencyStatusTypeSchema,
  countryOfCitizenship: countryOfCitizenshipSchema,
  countryOfBirth: countryOfBirthSchema,
  isUSPermanentResident: isUSPermanentResidentSchema,
  visaExpirationDate: visaExpirationDateSchema,
  visaType: visaTypeSchema,
  dateOfDepartureFromUS: dateOfDepartureFromUSSchema,
});

const updateUserResidencyStatusClientSchema = strictObjectSchema({
  residencyStatus: residencyStatusClientSchema.required(REQUIRED_SYSTEM_HANDLED),
});

const disclosureItemSchema = yup.boolean().required(REQUIRED_SYSTEM_HANDLED);
const disclosuresSchema = strictObjectSchema(
  Object.fromEntries(DisclosureItem.ALL_ITEMS.map((item) => [item, disclosureItemSchema])),
);

const updateUserDisclosuresSchema = strictObjectSchema({
  disclosures: disclosuresSchema.required(REQUIRED_SYSTEM_HANDLED),
});

const updateUserDisclosuresClientSchema = strictObjectSchema({
  disclosuresApply: yup.mixed().oneOf(['none', 'some']).label('disclosure').required(REQUIRED_SYSTEM_HANDLED),
  disclosures: yup
    .array()
    .of(yup.string().oneOf(DisclosureItem.ALL_ITEMS, UNEXPECTED_SYSTEM_HANDLED)) // Ensure each item is from DisclosureItem.ALL_ITEMS
    .test(ignoredTestName, 'validationMessage', function (value) {
      return this.parent.disclosuresApply == 'none' || (value && value.length >= 1);
    }),
});

const requestUserPasswordResetSchema = strictObjectSchema({
  email: emailRequiredSchema,
});

const updateUserEmailAddressSchema = strictObjectSchema({
  password: passwordSchema,
  email: emailRequiredSchema.test(ignoredTestName, 'notChanged', function (newEmail) {
    const { email } = this.options.context();
    return newEmail != email;
  }),
});

const updateUserPasswordSchema = strictObjectSchema({
  currentPassword: currentPasswordSchema,
  newPassword: newPasswordSchema,
  newPasswordRepeated: newPasswordRepeatedSchema,
});

const resetUserPasswordSchema = strictObjectSchema({
  newPassword: newPasswordSchema,
  newPasswordRepeated: newPasswordRepeatedSchema,
});

const updateUserPhoneNumberSchema = strictObjectSchema({
  phoneNumber: phoneNumberRequiredSchema,
  textMessagingApproved: textMessagingApprovedRequiredSchema,
});

const acceptedAgreementsSchema = yup
  .array()
  .of(
    strictObjectSchema({
      agreementName: yup.string().required(REQUIRED_SYSTEM_HANDLED),
    }),
  )
  .required(REQUIRED_SYSTEM_HANDLED);

const addAcceptedAgreementsClientSchema = strictObjectSchema({
  agreementsAccepted: yup.boolean().label('acceptTerms').oneOf([true], 'required').required('required'),

  acceptedAgreements: acceptedAgreementsSchema.min(1, UNEXPECTED_SYSTEM_HANDLED),
});

const addAcceptedAgreementsSchema = strictObjectSchema({
  acceptedAgreements: acceptedAgreementsSchema.test(
    ignoredTestName,
    UNEXPECTED_SYSTEM_HANDLED,
    function (acceptedAgreements) {
      const { requiredAgreements } = this.options.context();
      const requiredAgreementNames = requiredAgreements.map(({ agreementName }) => agreementName);
      const acceptedAgreementNames = acceptedAgreements.map(({ agreementName }) => agreementName);
      const missingAgreements = requiredAgreementNames.filter(
        (agreementName) => !acceptedAgreementNames.includes(agreementName),
      );
      return missingAgreements.length === 0;
    },
  ),
});

const userForSubmitInvestmentAccountSchema = strictObjectSchema({
  ...updateUserNameSchema.fields,
  ...updateUserPhoneNumberSchema.fields,
  ...updateUserAddressSchema.fields,
  ...updateUserBirthdaySchema.fields,
  ...updateUserTaxIdSchema.fields,
  ...updateUserResidencyStatusSchema.fields,
  ...updateUserEmploymentStatusSchema.fields,
  ...updateUserFundingSourceSchema.fields,
  ...updateUserDisclosuresSchema.fields,
  ...updateUserAccountLossRiskQuestionSchema.fields,
  ...updateUserInvestmentObjectiveRiskQuestionSchema.fields,
  ...updateUserIncomeLevelSchema.fields,
  ...updateUserNetWorthSchema.fields,
  ...addAcceptedAgreementsSchema.fields,
});

const maritalStatusSchema = yup
  .string()
  .label('maritalStatus')
  .oneOf(MaritalStatus.ALL_ITEMS, 'validationMessage')
  .required('required');

const updateUserMaritalStatusSchema = strictObjectSchema({ maritalStatus: maritalStatusSchema });

const beneficiaryRelationshipSchema = yup
  .string()
  .label('beneficiaryRelationship')
  .oneOf(BeneficiaryRelationship.ALL_ITEMS, UNEXPECTED_SYSTEM_HANDLED)
  .required('required');

const beneficiaryTypeSchema = yup
  .string()
  .label('beneficiaryType')
  //TODO review this logic and allow backup beneficiaries once alpaca questions addressed
  .oneOf([BeneficiaryType.PRIMARY], UNEXPECTED_SYSTEM_HANDLED)
  .required('required');

const sharePctSchema = yup
  .string()
  .label('beneficiarySharePct')
  .required('required')
  .test(ignoredTestName, 'validationMessage', function (valueString) {
    const valueInt = parseInt(valueString);
    if (valueInt.toString() != valueString) return false;
    const validValue = valueInt >= 1 && valueInt <= 100;
    if (!validValue) return false;

    const { beneficiaries } = this.options.context().data;
    const totalSharePct = beneficiaries.reduce((sum, beneficiary) => {
      return sum + parseInt(beneficiary.sharePct);
    }, 0);

    return totalSharePct == 100;
  });

const beneficiarySchema = strictObjectSchema({
  name: nameRequiredSchema,
  birthday: beneficiaryBirthdayRequiredSchema,
  taxId: taxIdRequiredSchema,
  relationship: beneficiaryRelationshipSchema,
  type: beneficiaryTypeSchema,
  sharePct: sharePctSchema,
});

const beneficiariesSchema = yup.array().of(beneficiarySchema).required(UNEXPECTED_SYSTEM_HANDLED);

const updateUserBeneficiariesSchema = strictObjectSchema({
  maritalStatus: maritalStatusSchema,
  beneficiaries: beneficiariesSchema,
});

const trustedContactSchema = strictObjectSchema({
  name: nameRequiredSchema,
  email: emailRequiredSchema,
  phoneNumber: phoneNumberOptionalSchema,
});

const updateUserTrustedContactSchema = strictObjectSchema({ trustedContact: trustedContactSchema });

const electionInteger = (label, lower, upper, relevantElectionType) =>
  yup
    .number()
    .label(label)
    .test(ignoredTestName, 'validValueWhenRequired', function (value) {
      const fieldIsRequired = this.parent.electionType === relevantElectionType;
      const isValid = isInteger(value) && value >= lower && value <= upper;
      return fieldIsRequired ? isValid : isEmpty(value);
    });

const PAYROLL_CONTRIBUTION_PERCENT_MIN = 0;
const PAYROLL_CONTRIBUTION_PERCENT_MAX = 25;
const PAYROLL_CONTRIBUTION_DOLLARS_MIN = 100;
const PAYROLL_CONTRIBUTION_DOLLARS_MAX = 900;

const payrollElectionSchema = strictObjectSchema({
  electionType: yup.string().label('electionType').oneOf(PayrollElectionType.ALL_ITEMS).required(),
  electionPercent: electionInteger(
    'electionPercent',
    PAYROLL_CONTRIBUTION_PERCENT_MIN,
    PAYROLL_CONTRIBUTION_PERCENT_MAX,
    PayrollElectionType.CONTRIBUTION_PERCENT,
  ),
  electionDollars: electionInteger(
    'electionDollars',
    PAYROLL_CONTRIBUTION_DOLLARS_MIN,
    PAYROLL_CONTRIBUTION_DOLLARS_MAX,
    PayrollElectionType.CONTRIBUTION_DOLLARS,
  ),
});

const updateUserEmploymentPayrollElectionSchema = strictObjectSchema({
  employeeRequestedPayrollElection: payrollElectionSchema,
});

const requiredToBeAdmin = [INVALID_ACCOUNT_OPERATION, (context) => context?.isPayrollAdmin === true];

const requiredToBeThirdPartyPayrollAdmin = [
  INVALID_ACCOUNT_OPERATION,
  (context) => context?.isThirdPartyPayrollAdmin === true,
];

const requiredToBeOnboardedAdmin = [
  INVALID_ACCOUNT_STATE_FOR_REQUEST,
  (context) => context?.onboardingComplete === true,
];

const payrollAdminTypeSchema = yup
  .string()
  .label('payrollAdminType')
  .oneOf(PayrollAdminType.ALL_ITEMS, UNEXPECTED_SYSTEM_HANDLED)
  .required('required');

const updateAdminTypeClientSchema = strictObjectSchema({
  payrollAdminType: payrollAdminTypeSchema,
});

//is third party payroll admin
const isThirdPartyPayrollAdminSchema = yup.boolean().required(REQUIRED_SYSTEM_HANDLED);

const updateUserIsThirdPartyPayrollAdminSchema = strictObjectSchema({
  isThirdPartyPayrollAdmin: isThirdPartyPayrollAdminSchema,
})
  .testContext(...requiredToBeAdmin)
  .testContext(INVALID_ACCOUNT_STATE_FOR_REQUEST, ({ onboardingComplete }) => !onboardingComplete);

//employer, admin's employer
const [, employerNameRequiredSchema] = makeOptionalAndRequiredSchemas(baseNameSchema.label('employerName'));

const titleOptionalSchema = yup.string().label('employerContactTitle').optional();

const employerContactOptionalSchema = strictObjectSchema({
  name: nameOptionalSchema,
  title: titleOptionalSchema,
  email: emailOptionalSchema,
  phoneNumber: phoneNumberOptionalSchema,
});

const employerSchema = strictObjectSchema({
  employerName: employerNameRequiredSchema,
  employerContact: employerContactOptionalSchema,
  employerAddress: addressRequiredSchema,
});

const updateUserAdminEmployerClientSchema = strictObjectSchema({
  adminEmployer: employerSchema.required(REQUIRED_SYSTEM_HANDLED),
});

const updateUserAdminEmployerSchema = updateUserAdminEmployerClientSchema.testContext(...requiredToBeAdmin);

const completeAdminOnboardingSchema = strictObjectSchema({
  isPayrollAdmin: yup.boolean().equals([true], REQUIRED_SYSTEM_HANDLED),
  ...updateUserNameSchema.fields,
  ...updateUserPhoneNumberSchema.fields,
  ...updateUserIsThirdPartyPayrollAdminSchema.fields,
  ...updateUserAdminEmployerSchema.fields,
  ...addAcceptedAgreementsSchema.fields,
}).testContext(INVALID_ACCOUNT_STATE_FOR_REQUEST, ({ onboardingComplete }) => !onboardingComplete);

export {
  //re-used by employer validations
  nameOptionalSchema,
  nameRequiredSchema,
  emailOptionalSchema,
  emailRequiredSchema,
  phoneNumberOptionalSchema,
  taxIdOptionalSchema,
  taxIdRequiredSchema,
  addressOptionalSchema,
  userBirthdayOptionalSchema,
  payrollElectionSchema,
  languagePreferenceOptionalSchema,
  languagePreferenceRequiredSchema,
  employerSchema,
  requiredToBeThirdPartyPayrollAdmin,
  requiredToBeOnboardedAdmin,

  //used by forms and controllers
  createAccountWithEmailAndPasswordSchema,
  createUserSchema,
  loginUserSchema,
  activateUserEmploymentSchema,
  updateUserNameClientSchema,
  updateUserNameSchema,
  updateUserLanguagePreferenceSchema,
  updateUserAddressSchema,
  updateUserBirthdayClientSchema,
  updateUserBirthdaySchema,
  updateUserTaxIdClientSchema,
  updateUserTaxIdSchema,
  updateUserFundingSourceSchema,
  updateUserDisclosuresSchema,
  updateUserDisclosuresClientSchema,
  requestUserPasswordResetSchema,
  updateUserEmailAddressSchema,
  updateUserPasswordSchema,
  resetUserPasswordSchema,
  updateUserPhoneNumberSchema,
  addAcceptedAgreementsClientSchema,
  addAcceptedAgreementsSchema,
  userForSubmitInvestmentAccountSchema,
  updateUserMaritalStatusSchema,
  updateUserBeneficiariesSchema,
  updateUserTrustedContactSchema,
  updateUserEmploymentPayrollElectionSchema,
  updateUserEmploymentStatusSchema,
  updateUserAccountLossRiskQuestionSchema,
  updateUserInvestmentObjectiveRiskQuestionSchema,
  updateUserIncomeLevelSchema,
  updateUserNetWorthSchema,
  updateUserResidencyStatusSchema,
  updateUserResidencyStatusClientSchema,
  updateAdminTypeClientSchema,
  updateUserIsThirdPartyPayrollAdminSchema,
  updateUserAdminEmployerClientSchema,
  updateUserAdminEmployerSchema,
  completeAdminOnboardingSchema,

  //constants used for UI and testing
  calculateAge,
  USER_AGE_MIN,
  USER_AGE_MAX,
  PAYROLL_CONTRIBUTION_PERCENT_MIN,
  PAYROLL_CONTRIBUTION_PERCENT_MAX,
  PAYROLL_CONTRIBUTION_DOLLARS_MIN,
  PAYROLL_CONTRIBUTION_DOLLARS_MAX,

  //used for direct testing of schema
  residencyStatusSchema,
};
